/**
 * Router Configuration
 *
 * This module builds the React Router configuration from `portal-config.json`.
 * It uses React Router's `handle` property to attach app configuration to routes,
 * enabling any component to access the active app's config via `useMatches()`.
 *
 * ## Route-Config Binding Pattern
 *
 * The portal dynamically creates routes from the `apps` array in portal-config.json.
 * Each app config is attached to its route via the `handle` property:
 *
 * ```
 * portal-config.json                    Route Object
 * ──────────────────                    ────────────
 * {                                     {
 *   "path": "/app-template/*",               path: "/app-template/*",
 *   "basePath": "/app-template",      →      element: <PageHost config={...} />,
 *   "appName": "App Template",              handle: { appConfig: { ... } }
 *   "mfPageMount": "app_template/..."      }
 * }
 * ```
 *
 * ## Accessing Config from Components
 *
 * Any component can access the active app config using React Router's `useMatches()`:
 *
 * ```tsx
 * function MyComponent() {
 *     const matches = useMatches();
 *     const appConfig = matches
 *         .map(m => (m.handle as RouteHandle)?.appConfig)
 *         .filter(Boolean)
 *         .pop();  // Get deepest match (child overrides parent)
 *
 *     return <div>Current app: {appConfig?.appName}</div>;
 * }
 * ```
 *
 * ## Why This Pattern?
 *
 * 1. **Config-driven routing** - Routes are generated from JSON config, not hardcoded
 * 2. **Decoupled components** - Components don't need to know URL patterns
 * 3. **Type-safe** - RouteHandle interface ensures consistent shape
 * 4. **Nested route support** - `useMatches()` returns all matched routes, so child
 *    routes can override parent configs
 *
 * @see ShellLayout - Main consumer that configures sidebar/header based on active app
 * @see PageHost - Renders the federated module for each app route
 */

import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { env, type AppConfig } from './env';
import { PageHost } from '@/components/module-federation/page-host/page-host';
import { RootLayout } from '../components/layouts/root-layout';
import { AuthLayout } from '../components/layouts/auth-layout';
import { ShellLayout } from '../components/layouts/shell-layout';
import { PortalHrdLogin, isAuthDisabled } from '@/auth';

/**
 * Route handle containing app-specific configuration.
 *
 * This interface defines the shape of data attached to routes via the `handle` property.
 * React Router's `useMatches()` hook returns all matched routes with their handles,
 * allowing any component to access the active app's configuration.
 *
 * @example
 * ```tsx
 * // In a route definition:
 * { path: '/app/*', element: <App />, handle: { appConfig: myAppConfig } }
 *
 * // In a component:
 * const matches = useMatches();
 * const config = (matches[matches.length - 1].handle as RouteHandle)?.appConfig;
 * ```
 */
export interface RouteHandle {
    appConfig?: AppConfig;
}

/**
 * Builds route objects from the apps defined in portal-config.json.
 *
 * Each app in the config becomes a route with:
 * - `path`: URL pattern from config (e.g., "/app-template/*")
 * - `element`: PageHost component that loads the federated module
 * - `handle`: Contains the full AppConfig for use by shell components
 *
 * The `handle.appConfig` is the key mechanism that connects:
 * - portal-config.json → Routes → ShellLayout → Sidebar/Header configuration
 *
 * @example
 * Given this config:
 * ```json
 * {
 *   "apps": [{
 *     "path": "/app-template/*",
 *     "basePath": "/app-template",
 *     "appName": "App Template",
 *     "remotePageMount": "app_template/getRemotePageMount"
 *   }]
 * }
 * ```
 *
 * Produces this route:
 * ```typescript
 * {
 *   path: "/app-template/*",
 *   element: <PageHost config={appConfig} />,
 *   handle: { appConfig: { path: "/app-template/*", appName: "App Template", ... } }
 * }
 * ```
 *
 * @returns Array of RouteObject for each valid app in the config
 */
function buildAppRoutes(): RouteObject[] {
    return env.apps
        .filter(app => app.importRemotePageMount && app.path && app.basePath !== undefined)
        .map(app => ({
            path: app.path,
            element: <PageHost config={app} />,
            handle: { appConfig: app } satisfies RouteHandle
        }));
}

/**
 * Not found component for unmatched routes.
 */
function NotFound() {
    return (
        <div className="p-8 text-center">
            <h2 className="mb-4 text-xl font-semibold">404</h2>
            <p>The page you&apos;re looking for doesn&apos;t exist or hasn&apos;t been configured.</p>
        </div>
    );
}

/**
 * Builds the complete router from environment configuration.
 *
 * Route structure:
 * - RootLayout (error boundary, suspense)
 *   - /login (public, only when auth enabled)
 *   - AuthLayout (auth protection)
 *     - ShellLayout (sidebar + header)
 *       - App routes from config
 *       - * (not found)
 */
export function buildRouter() {
    const appRoutes = buildAppRoutes();

    // Build the protected routes (inside AuthLayout > ShellLayout)
    const protectedRoutes: RouteObject[] = [
        {
            element: <ShellLayout />,
            children: [
                ...appRoutes,
                {
                    path: '*',
                    element: <NotFound />
                }
            ]
        }
    ];

    // Build the root routes
    const rootChildren: RouteObject[] = [];

    // Add login route if auth is enabled
    if (!isAuthDisabled) {
        rootChildren.push({
            path: '/login',
            element: <PortalHrdLogin />
        });
    }

    // Add protected routes wrapped in AuthLayout
    rootChildren.push({
        element: <AuthLayout />,
        children: protectedRoutes
    });

    return createBrowserRouter([
        {
            element: <RootLayout />,
            children: rootChildren
        }
    ]);
}
